import { useState } from 'react';

const philosophyItems = [
  {
    icon: 'ri-heart-line',
    title: '陪伴而非督促',
    desc: '學習的速度不是最重要的事，重要的是在這個過程裡，你有沒有感到被看見、被支持。',
  },
  {
    icon: 'ri-leaf-line',
    title: '慢慢來，也是前進',
    desc: '沒有固定的進度表，也沒有「你應該在幾個月內學會什麼」。每個人的節奏都值得被尊重。',
  },
  {
    icon: 'ri-music-2-line',
    title: '從感受出發',
    desc: '不從樂理和規則開始，而從讓你喜歡這個聲音開始。技巧是工具，感受才是音樂的起點。',
  },
  {
    icon: 'ri-chat-smile-2-line',
    title: '不強銷、不比較',
    desc: '這裡沒有需要追趕的標準，也沒有讓你感到壓力的推銷。你的選擇，永遠由你自己做。',
  },
];

export default function TeacherSection() {
  const [activePhil, setActivePhil] = useState(0);

  return (
    <section className="py-24 md:py-32 bg-[#f7f3ee]" id="teacher">
      <div className="max-w-6xl mx-auto px-6 md:px-12 lg:px-20">

        {/* Section label */}
        <div className="flex items-center gap-3 mb-14">
          <div className="w-8 h-px bg-[#b09a80]"></div>
          <span className="text-[0.8125rem] tracking-[0.22em] text-[#9a8878] uppercase font-medium">師資與理念</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-20 items-start">

          {/* Left: Photo + bio */}
          <div>
            {/* Photo */}
            <div className="relative mb-10">
              <div className="relative overflow-hidden rounded-2xl">
                <img
                  src="https://storage.helloreaddy.io/project_files/79c1f631-1cb7-456b-a6cf-fdd8ff21e9a0/88a66b4c-4fd0-4fad-90f3-0fb7da6e2cf9_compressed_C--Image-202695-04_01_06.webp"
                  alt="鳴箏老師"
                  className="w-full aspect-[5/6] object-cover object-top"
                />
                {/* subtle overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#2c2018]/15 to-transparent pointer-events-none"></div>
              </div>
              {/* Floating quote card */}
              <div className="absolute -bottom-5 -right-4 md:right-6 max-w-[220px] bg-white/90 backdrop-blur-sm rounded-2xl p-4 border border-[#e8ddd0]">
                <p className="text-[0.8125rem] text-[#7a6248] leading-[1.7] font-medium">
                  「音樂是用來陪伴你的，不是讓你去追它的。」
                </p>
              </div>
            </div>

            {/* Name + intro */}
            <div className="pl-1">
              <h2 className="font-serif-tc text-[1.6rem] text-[#2c2018] tracking-[0.12em] mb-1">鳴箏老師</h2>
              <p className="text-[0.875rem] text-[#9a8878] tracking-[0.12em] mb-5">古箏教學・陪伴式音樂引導</p>
              <p className="text-[0.9375rem] text-[#5a4a3a] leading-[1.9] mb-4">
                從事古箏教學多年，教過的學生涵蓋五歲的孩子到五十歲的大人。不同的年齡、不同的起點、不同的理由想學古箏——但我發現每個人心裡都有一個共同的渴望：<strong className="font-medium text-[#3a2e22]">想要一件讓自己安靜下來的事。</strong>
              </p>
              <p className="text-[0.9375rem] text-[#5a4a3a] leading-[1.9]">
                我不以速度衡量學習，不以成果評估學生。我在意的是：你坐在箏前面的時候，有沒有感到安心？學完一堂課離開的時候，有沒有比來的時候輕鬆一點點？
              </p>

              {/* Tags */}
              <div className="flex flex-wrap gap-2 mt-6">
                {['古箏演奏', '陪伴式教學', '成人初學', '兒童教學', '選箏諮詢'].map((tag) => (
                  <span key={tag} className="px-3 py-1 text-[0.8125rem] text-[#7a6248] bg-[#ede5d8] rounded-full">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Right: Philosophy */}
          <div>
            <h3 className="font-serif-tc text-[1.35rem] text-[#2c2018] tracking-[0.1em] mb-3">
              我的教學信念
            </h3>
            <p className="text-[0.9375rem] text-[#6a5a4a] leading-[1.85] mb-10">
              在鳴箏，我選擇一種不一樣的教學方式——不是因為這樣比較「有效率」，而是因為我真的相信，讓音樂以這樣的姿態進入一個人的生活，才是長久且值得的事。
            </p>

            {/* Philosophy cards */}
            <div className="flex flex-col gap-3 mb-10">
              {philosophyItems.map((item, idx) => (
                <button
                  key={idx}
                  onClick={() => setActivePhil(idx)}
                  className={`w-full text-left rounded-2xl p-5 transition-all duration-300 cursor-pointer border ${
                    activePhil === idx
                      ? 'bg-[#f0e6d6] border-[#c8b49a]'
                      : 'bg-white border-[#ede5d8] hover:bg-[#f7f2ea] hover:border-[#ddd0bc]'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 transition-colors duration-300 ${
                      activePhil === idx ? 'bg-[#b09a80] text-white' : 'bg-[#ede5d8] text-[#8a7260]'
                    }`}>
                      <i className={`${item.icon} text-base`}></i>
                    </div>
                    <div>
                      <p className={`text-[0.9375rem] font-semibold mb-1.5 transition-colors duration-200 ${
                        activePhil === idx ? 'text-[#3a2e22]' : 'text-[#5a4a3a]'
                      }`}>{item.title}</p>
                      <p className={`text-[0.875rem] leading-[1.75] transition-all duration-300 ${
                        activePhil === idx ? 'text-[#6a5a4a] max-h-20 opacity-100' : 'text-[#9a8878] max-h-0 overflow-hidden opacity-0 lg:max-h-20 lg:opacity-100'
                      }`}>{item.desc}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {/* CTA */}
            <div className="bg-[#2c2018] rounded-2xl p-6">
              <p className="font-serif-tc text-white text-[1.1rem] tracking-[0.1em] mb-2">想先聊聊也可以</p>
              <p className="text-[#c8b49a] text-[0.875rem] leading-[1.75] mb-5">
                加入 LINE@ 之後不代表要報名。我更想先了解你的狀態，再一起看看這裡是不是適合你的地方。
              </p>
              <a
                href="https://line.me"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-[#2c2018] text-[0.875rem] font-medium hover:bg-[#f5f0e8] transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-line-fill text-[#06C755]"></i>
                加入 LINE@ 諮詢
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
